import * as React from 'react';
import { FC, useState, useEffect, CSSProperties, ReactNode } from 'react';
import { RejectedState, PENDING, REJECTED, FULFILLED } from 'admin-mojo/helpers/StateNotification';
import isGameWithJackpot from 'app/CasinoGames/isGameWithJackpot';
import LimitWarning from './LimitWarning';
import JackpotAggregatorCampaign from './JackpotAggregatorCampaign';
import useLauncher from './useLauncher';
import type { Limit, Launcher, LauncherProps } from './types';
import useLimits from './useLimits';

const DELAY_FOR_JACKPOTS = 3000;

const PendingSpinner: FC = () => (
  <div className="pending" style={{ opacity: 0 }}>
    <img src="/images/gameui/redstar-logo-red.svg" alt="Red Star Casino" />
    <div className="spinner" />
  </div>
);

const Loading: FC<{ campaign: ReactNode }> = ({ campaign }) => (
  <>
    <PendingSpinner />
    {campaign}
  </>
);

const Error: FC<{ state: RejectedState; campaign: ReactNode }> = ({ state, campaign }) => (
  <>
    <div className="error">{state.error || 'Error on game launch'}</div>
    {campaign}
  </>
);

const GameFrame: FC<{ launcher: Launcher }> = ({ launcher }) =>
  launcher.hasLaunchScript ? (
    <iframe title="game" id="game_iframe" src="about:blank" width="100%" height="100%" />
  ) : (
    <iframe title="game" src={launcher.launchUrl || ''} width="100%" height="100%" />
  );

const MobileContent: FC<{
  campaign: ReactNode;
}> = ({ campaign }) => (
  <div id="casino-game-launcher" className={campaign ? 'have-jackpot' : ''} aria-label="mobile-preloader">
    <div className="game-content">
      <div className="preload-info">
        <Loading campaign={campaign} />
      </div>
    </div>
  </div>
);

const useDelay = (haveCampaign: boolean) => {
  const [done, setDone] = useState(!haveCampaign);
  const delay = haveCampaign ? DELAY_FOR_JACKPOTS : 0;
  useEffect(() => {
    const timer: number = window.setTimeout(() => {
      setDone(true);
    }, delay);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [haveCampaign]);

  return done;
};

const Content: FC<{
  launcher: Launcher;
  achievedLimit: Limit | undefined;
  campaign: ReactNode;
  haveCampaign: boolean;
}> = ({ launcher, achievedLimit, campaign, haveCampaign }) => {
  const isDelayComplete = useDelay(haveCampaign);

  return (
    <div id="casino-game-launcher" className={haveCampaign ? 'have-jackpot' : ''}>
      <div className="game-content">
        {achievedLimit ? (
          <LimitWarning reason={achievedLimit.reason} />
        ) : (
          <>
            <div className="preload-info">
              <Loading campaign={campaign} />
            </div>
            {isDelayComplete && <GameFrame launcher={launcher} />}
          </>
        )}
        <div className="overbar">
          {launcher.jurisdiction}
          <br />
        </div>
      </div>
    </div>
  );
};

const fillInGameIframe = (launchUrl: string) => {
  const frame = document.getElementById('game_iframe') as HTMLIFrameElement;
  frame.dataset.filled = 'true'; // just for tests
  const frameDoc = frame.contentWindow?.document as Document;
  const prc100 = '$1100%';
  const html = launchUrl.replace(/(width.*?)\d+/, prc100).replace(/(height.*?)\d+/, prc100);
  (frameDoc.body.style as CSSProperties).margin = 0;
  frameDoc.body.style.overflow = 'hidden';
  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();
};

const LauncherComponent: FC<LauncherProps> = (props) => {
  const { mobileVisitor, game, jackpotAggregatorCampaigns } = props;
  const launcher = useLauncher(props);
  // eslint-disable-next-line prefer-const
  let [isLimitCalculated, achievedLimit] = useLimits(launcher);
  const [gameIsRendered, setGameIsRendered] = useState(false);
  const { state, playMode, launchUrl, hasLaunchScript } = launcher;
  if (playMode !== 'real') achievedLimit = undefined;

  useEffect(() => {
    if (state.status !== FULFILLED) return;

    if (hasLaunchScript && !gameIsRendered && launchUrl) {
      setGameIsRendered(true);
      fillInGameIframe(launchUrl);
    }
  }, [state.status]);

  const campaign = (): ReactNode =>
    isGameWithJackpot(game.type) ? <JackpotAggregatorCampaign campaigns={jackpotAggregatorCampaigns} /> : null;

  const haveCampaign = isGameWithJackpot(game.type) && jackpotAggregatorCampaigns.length > 0;
  const isDelayComplete = useDelay(haveCampaign);

  useEffect(() => {
    if (mobileVisitor && isLimitCalculated && !achievedLimit && launchUrl && isDelayComplete) {
      window.location.assign(launchUrl);
    }
  }, [isLimitCalculated, isDelayComplete]);

  switch (true) {
    case state.status === FULFILLED && (!mobileVisitor || !!achievedLimit):
      return (
        <Content launcher={launcher} achievedLimit={achievedLimit} campaign={campaign()} haveCampaign={haveCampaign} />
      );
    case state.status === FULFILLED && mobileVisitor:
      return <MobileContent campaign={campaign()} />;
    case state.status === PENDING:
      return <Loading campaign={campaign()} />;
    case state.status === REJECTED:
      return <Error state={state as RejectedState} campaign={campaign()} />;
    default:
      return null;
  }
};

export default LauncherComponent;
